/* eslint-disable max-len */
import axios from "axios"
import classNames from "classnames"
import { List, fromJS } from "immutable"
import moment from "moment"
import React from "react"
import { createSelector, createStructuredSelector } from "reselect"

// components
import ResourceTableIssueAdder from "app/static/frontend/components/ResourceTableIssueAdder"
import { DropdownButton, Icon } from "quorumdesign"
import IconTypeCell from "sheets/components/cells/IconTypeCell"

// action creators
import { updateCellExtraAndPatchValue } from "app/static/frontend/search-new/containers/TableView/action-creators"
import reportPerson from "imports/reportPerson"

// selectors
import { selectCurrentFilters, selectSearchFlvSlice } from "shared/search/selectors"
import { selectAlignmentTypesAsEnum } from "shared/stakeholder/selectors"
import { selectIsPacPersona, selectIsPacHybridPersona } from "shared/userdata/userdata-selectors"
// constants
import * as colors from "app/static/frontend/design-constants"
import {
    columnWidthConstants,
    dataTypesWithoutColumnSorting,
    dataTypesWithoutRowLinks,
    feedItemDataTypesWithAlignment,
    feedItemDataTypesWithPriority,
    feedItemDataTypesWithStance,
} from "app/static/frontend/search-new/containers/TableView/constants"
import urls from "imports/desktopPaths"
import { DISBURSEMENT_ENDPOINT, RECEIPT_ENDPOINT } from "shared/djangio/moneyballHelpers"
import { selectHasMultipleBankAccounts, selectFirstLedgerSettingsId } from "shared/pac/selectors"

// helper functions
import {
    getActionColumnConfig,
    getBasicColumnConfig,
    getDateColumnConfig,
    getDollarsColumnConfig,
} from "app/static/frontend/search-new/containers/TableView/helperFunctions"
import sheetConstants from "app/static/frontend/sheets/constants"
import { archiveSupporterAction } from "app/static/frontend/sheets/sheet-action-creators"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { checkPermission, hasAnyPermission } from "shared/imports/permissionFunctions"
import {
    capitalizeFirstLetter,
    cleanUpHtml,
    formatPhoneNumber,
    parseTimezoneNameFromAbbrev,
    parseEstDateToTimezone,
} from "shared/imports/sharedHelperFunctions"
import { canEditNote } from "shared/search/helperFunctions"
import { donationTypeValues } from "shared/search/inline-selectors/inline-options-selectors"
import { getEventChamber, getFeedItemUnderlyingItemId } from "shared/search/inline-selectors/inline-selectors"
import { stanceWithCustomLabelByValue } from "shared/stance/helperFunctions"
import TransactionTypeCell from "sheets/components/cells/TransactionTypeCell"
import NumberCounterCell from "app/static/frontend/sheets/components/resource-tables/NumberCounterCell"
import { selectActionCentersGamified } from "shared/grassroots/selectors"

import { isGrassrootsPersona } from "shared/grassroots/helperFunctions"

const { QuorumDataType } = DjangIO.app.models
const { PermissionType } = DjangIO.app.models

export const selectQuorumDataType = (state, props) => props.quorumDataType

export const formatDate = (date) => {
    const dateParts = date.split("-")
    const month = dateParts[1]
    const day = dateParts[2]
    const year = dateParts[0]
    const formattedDate = `${month}/${day}/${year}`
    return formattedDate
}

function formatAmount(amount) {
    const formattedAmount = Math.abs(parseFloat(amount))
        .toFixed(2)
        .replace(/\d(?=(\d{3})+\.)/g, "$&,")

    if (amount < 0) {
        return `-$${formattedAmount}` || "-$0.00"
    } else {
        return `$${formattedAmount}` || "$0.00"
    }
}

export const selectTableId = createSelector(
    selectQuorumDataType,
    (quorumDataType) =>
        quorumDataType && `${DjangIO.app.models.QuorumDataType.by_value(quorumDataType).plural}-search-table-view`,
)

export const selectActionColumn = createSelector(
    (state, props) => props,
    selectQuorumDataType,
    selectTableId,
    selectFirstLedgerSettingsId,
    (props, quorumDataType, tableId, ledgerSettingsId) => {
        switch (quorumDataType) {
            case QuorumDataType.attachment.value:
                return getActionColumnConfig({
                    getMenuOptions: (id) => [
                        {
                            icon: "download",
                            iconFamily: "far",
                            text: "Download",
                            href: `${urls.outboxDownloadPrefix}${id}/`,
                        },
                        {
                            icon: "trash",
                            iconFamily: "far",
                            text: "Archive",
                            onClick: () => props.removeResourceTableRow(tableId, id),
                        },
                    ],
                })
            case QuorumDataType.bill.value:
                return getActionColumnConfig({
                    field: ["id", "source_link"],
                    getMenuOptions: (id, sourceLink) => {
                        const menuOptions = [
                            {
                                icon: "pencil-alt",
                                iconFamily: "far",
                                text: "Edit",
                                onClick: () => props.segueToEditBill(id),
                            },
                        ]
                        if (sourceLink) {
                            menuOptions.push({
                                icon: "external-link",
                                iconFamily: "far",
                                text: "Source",
                                href: sourceLink,
                                onClick: (event) => event.stopPropagation(),
                            })
                        }
                        return menuOptions
                    },
                })
            case QuorumDataType.grassroots_action.value:
                return getActionColumnConfig({
                    getMenuOptions: (id) => [
                        {
                            icon: "trash",
                            iconFamily: "far",
                            text: "Archive",
                            onClick: () => props.removeResourceTableRow(tableId, id),
                        },
                    ],
                })
            case QuorumDataType.note.value:
                return getActionColumnConfig({
                    field: [
                        "id",
                        "interaction_type",
                        "relationship_type",
                        "comment_type",
                        "auto_created",
                        "user_id",
                        "is_collaborator_comment",
                    ],
                    getMenuOptions: (
                        id,
                        interactionType,
                        relationshipType,
                        commentType,
                        autoCreated,
                        creatorId,
                        isCollaboratorComment,
                    ) => {
                        const interactionTypeEnum = DjangIO.app.userdata.types.InteractionType.by_value(interactionType)
                        const menuOptions = []
                        if (canEditNote(interactionTypeEnum, creatorId, autoCreated, isCollaboratorComment)) {
                            menuOptions.push({
                                icon: "pencil-alt",
                                iconFamily: "far",
                                text: "Edit",
                                onClick: () => {
                                    if (relationshipType) {
                                        props.segueToEditRelationship(id)
                                    } else if (commentType) {
                                        props.segueToEditComment(id)
                                    } else if (donationTypeValues.includes(interactionType)) {
                                        props.segueToEditDonation(id)
                                    } else {
                                        props.segueToEditInteraction(id)
                                    }
                                },
                            })
                        }
                        menuOptions.push({
                            icon: "trash",
                            iconFamily: "far",
                            text: "Archive",
                            onClick: () => props.removeResourceTableRow(tableId, id),
                        })
                        menuOptions.push({
                            icon: "download",
                            iconFamily: "far",
                            text: "Download",
                            href: `/api/note/${id}/?ics=true`,
                            onClick: (event) => event.stopPropagation(),
                        })
                        return menuOptions
                    },
                })
            case QuorumDataType.person.value:
                return getActionColumnConfig({
                    field: ["id", "organization", "user", "_extra"],
                    getMenuOptions: (id, org, user, extra) => [
                        ...(org || user
                            ? []
                            : [
                                  {
                                      icon: "flag",
                                      iconFamily: "far",
                                      text: "Suggest Edit",
                                      onClick: () => reportPerson(id),
                                  },
                              ]),
                        {
                            icon: "external-link",
                            iconFamily: "far",
                            text: "Visit Website",
                            onClick: () => {
                                const website = extra.getIn(["most_recent_role", "most_recent_role__website"])
                                window.open(website)
                            },
                        },
                        {
                            icon: "pencil-alt",
                            iconFamily: "far",
                            text: "Edit",
                            onClick: () => props.segueToEditOfficial(id),
                        },
                        {
                            icon: "download",
                            iconFamily: "far",
                            text: "Download",
                            onClick: (e) => {
                                const downloadUrl = DjangIO.app.person.models.Person.objects
                                    .get(id)
                                    .action("vcf")
                                    .buildUrl()
                                window.location = downloadUrl
                                e.stopPropagation()
                            },
                        },
                    ],
                })
            case QuorumDataType.public_organization.value:
                return getActionColumnConfig({
                    field: ["id", "organization_type", "_ph_sector_type"],
                    display: (id, organizationType) => (
                        <div className="action-button">
                            <DropdownButton
                                text="Actions"
                                menuOptions={[
                                    {
                                        icon: "pencil-alt",
                                        iconFamily: "far",
                                        text: "Edit",
                                        onClick:
                                            organizationType ===
                                            DjangIO.app.person.types.PublicOrganizationType.custom.value
                                                ? () => props.segueToEditOrganization(id)
                                                : () => props.segueToModifyOrganization(id),
                                    },
                                ]}
                                isTiny
                                type="action-idle"
                            />
                        </div>
                    ),
                })
            case QuorumDataType.regulation.value:
                return getActionColumnConfig({
                    getMenuOptions: (id) => [
                        {
                            icon: "pencil-alt",
                            iconFamily: "far",
                            text: "Edit",
                            onClick: () => props.segueToEditRegulation(id),
                        },
                    ],
                })
            case QuorumDataType.staffer.value:
                return getActionColumnConfig({
                    getMenuOptions: (id) => [
                        {
                            icon: "flag",
                            iconFamily: "far",
                            text: "Suggest Edit",
                            onClick: () => reportPerson(id),
                        },
                        {
                            icon: "pencil-alt",
                            iconFamily: "far",
                            text: "Edit",
                            onClick: () => props.segueToEditOfficial(id),
                        },
                        {
                            icon: "download",
                            iconFamily: "far",
                            text: "Download",
                            onClick: (e) => {
                                const downloadUrl = DjangIO.app.person.models.Person.objects
                                    .get(id)
                                    .action("vcf")
                                    .buildUrl()
                                window.location = downloadUrl
                                e.stopPropagation()
                            },
                        },
                        {
                            icon: "envelope",
                            iconFamily: "far",
                            text: "Outbox",
                            onClick: () => props.segueToOutboxCompose(id),
                        },
                    ],
                })
            case QuorumDataType.supporter.value:
                return getActionColumnConfig({
                    field: ["id", "is_collaborator_supporter"],
                    getMenuOptions: (id, isCollaboratorSupporter) => {
                        const canEditCollaborator = !(isCollaboratorSupporter && !Userdata.is_admin)
                        const menuOptions = []
                        if (canEditCollaborator) {
                            menuOptions.push({
                                icon: "pencil-alt",
                                iconFamily: "far",
                                text: "Edit",
                                onClick: () => props.segueToEditContact(id),
                            })
                        }
                        menuOptions.push({
                            icon: "trash",
                            iconFamily: "far",
                            text: "Archive",
                            onClick: () => props.removeResourceTableRow(tableId, id, archiveSupporterAction),
                        })
                        menuOptions.push({
                            icon: "download",
                            iconFamily: "far",
                            text: "Download",
                            onClick: (e) => {
                                const downloadUrl = DjangIO.app.grassroots.models.Supporter.objects
                                    .get(id)
                                    .action("vcf")
                                    .buildUrl()
                                window.location = downloadUrl
                                e.stopPropagation()
                            },
                        })
                        menuOptions.push({
                            icon: "envelope",
                            iconFamily: "far",
                            text: "Outbox",
                            onClick: () => props.segueToOutboxComposeSupporter(id),
                        })
                        return menuOptions
                    },
                })
            case QuorumDataType.receipt.value:
                return getActionColumnConfig({
                    field: ["id", "filing_source_url"],
                    getMenuOptions: (id) => [
                        {
                            icon: "file-pdf",
                            iconFamily: "far",
                            text: "View PDF",
                            onClick: (e) => {
                                const pdfUrl = `${RECEIPT_ENDPOINT}/${id}.pdf`

                                axios
                                    .head(pdfUrl)
                                    .then(() => window.open(pdfUrl, "_blank"))
                                    .catch(() =>
                                        swal({
                                            title: "Error",
                                            text: "The original filing appears to be unavailable",
                                            icon: "error",
                                        }),
                                    )

                                e.stopPropagation()
                            },
                        },
                    ],
                })
            case QuorumDataType.disbursement.value:
                return getActionColumnConfig({
                    field: ["id", "disbursement_id"],
                    getMenuOptions: (id, disbursement_id) => [
                        {
                            icon: "file-pdf",
                            iconFamily: "far",
                            text: "View PDF",
                            onClick: (e) => {
                                const pdfUrl = `${DISBURSEMENT_ENDPOINT}/${disbursement_id}.pdf`

                                axios
                                    .head(pdfUrl)
                                    .then(() => window.open(pdfUrl, "_blank"))
                                    .catch(() =>
                                        swal({
                                            title: "Error",
                                            text: "The original filing appears to be unavailable",
                                            icon: "error",
                                        }),
                                    )

                                e.stopPropagation()
                            },
                        },
                    ],
                })
            case QuorumDataType.political_committee.value:
                if (ledgerSettingsId) {
                    return getActionColumnConfig({
                        field: [
                            "id",
                            "interaction_type",
                            "relationshipType",
                            "comment_type",
                            "auto_created",
                            "user_id",
                            "organization",
                        ],
                        shouldHide: (
                            id,
                            interactionType,
                            relationshipType,
                            commentType,
                            autoCreated,
                            creatorId,
                            organization,
                        ) => {
                            return !(
                                checkPermission(DjangIO.app.models.PermissionType.pac_state_and_local_committees) &&
                                organization
                            )
                        },
                        getMenuOptions: (
                            id,
                            interactionType,
                            relationshipType,
                            commentType,
                            autoCreated,
                            creatorId,
                            organization,
                        ) => {
                            const menuOptions = []

                            if (
                                checkPermission(DjangIO.app.models.PermissionType.pac_state_and_local_committees) &&
                                organization
                            ) {
                                menuOptions.push({
                                    icon: "pencil-alt",
                                    iconFamily: "far",
                                    text: "Edit",
                                    onClick: () => {
                                        props.segueToEditPoliticalCommittee(id, ledgerSettingsId)
                                    },
                                })
                            }

                            return menuOptions
                        },
                    })
                }

                return {}

            default:
                return {}
        }
    },
)

export const selectPriorityColumn = createSelector(
    (state, props) => props,
    selectQuorumDataType,
    selectTableId,
    (props, quorumDataType, tableId) => ({
        name: "Priority",
        field: ["id", "_extra", "data_type", "big_cache"],
        display: (id, extra, underlyingQDT, feedItemCache) => {
            const currentPriorityValue =
                extra.get("priority") ||
                // for FeedItems we use dehydrate_extra_priority_stance_alignment
                extra.getIn(["priority_stance_alignment", "priority"]) ||
                DjangIO.app.userdata.types.LegislativePriorityType.no_priority.value

            // this is special logic for the alerts table because each row has an underlying object
            const quorumLink =
                feedItemCache &&
                feedItemCache.get("quorum_link") &&
                feedItemCache.get("quorum_link").replace("https://www.quorum.us", "")
            const underlyingDatumId = feedItemCache && getFeedItemUnderlyingItemId(quorumLink, underlyingQDT)
            if (
                quorumDataType === QuorumDataType.alert.value &&
                !feedItemDataTypesWithPriority.includes(underlyingQDT)
            ) {
                return null
            }

            return (
                <div className={classNames("action-button", "search-table-dropdown")}>
                    <DropdownButton
                        text={
                            currentPriorityValue ===
                            DjangIO.app.userdata.types.LegislativePriorityType.no_priority.value
                                ? DjangIO.app.userdata.types.LegislativePriorityType.by_value(currentPriorityValue)
                                      .label
                                : DjangIO.app.userdata.types.LegislativePriorityType.by_value(
                                      currentPriorityValue,
                                  ).label.replace(" Priority", "")
                        }
                        leftIcon={
                            DjangIO.app.userdata.types.LegislativePriorityType.by_value(currentPriorityValue).fa5_icon
                        }
                        leftIconFamily={
                            currentPriorityValue ===
                            DjangIO.app.userdata.types.LegislativePriorityType.no_priority.value
                                ? "fal"
                                : "fas"
                        }
                        menuOptions={DjangIO.app.userdata.types.LegislativePriorityType.items()
                            .filter((priority) => priority.value !== currentPriorityValue)
                            .filter((item) => item.value)
                            .map((priority) => ({
                                iconFamily: "fas",
                                icon: priority.fa5_icon,
                                text: priority.label.replace(" Priority", ""),
                                dataCy: priority.key,
                                onClick: () =>
                                    updateCellExtraAndPatchValue({
                                        updateResourceTableRow: props.updateResourceTableRow,
                                        tableId,
                                        rowId: id,
                                        patchValueFunction: props.setOrCreatePriority,
                                        patchValueArgs: {
                                            priority: priority.value,
                                            object_id: underlyingDatumId || id,
                                            quorum_data_type: underlyingQDT || quorumDataType,
                                        },
                                        updateExtraObj: { priority: priority.value },
                                        updateExtraRollbackObj: { priority: currentPriorityValue },
                                    }),
                            }))
                            .sort(DjangIO.valueSort)}
                        isTiny
                        type="resource-table-dropdown"
                        dataCy="search-table-priority-dropdown"
                    />
                </div>
            )
        },
        width: columnWidthConstants.STANDARD_COLUMN_WIDTH,
        resourceTableEnableSorting: !dataTypesWithoutColumnSorting.includes(quorumDataType),
        resourceTableDisableCellLink: true,
        columnSortingQuerymethod: "order_by_priority",
    }),
)

export const selectAlignmentColumn = createSelector(
    (state, props) => props,
    selectQuorumDataType,
    selectTableId,
    selectAlignmentTypesAsEnum,
    (props, quorumDataType, tableId, alignmentTypes) => ({
        name: "Alignment",
        field: ["id", "_extra", "data_type", "big_cache"],
        display: (id, extra, underlyingQDT, feedItemCache) => {
            const alignmentId =
                extra.get("alignment") ||
                // for FeedItems we use dehydrate_extra_priority_stance_alignment
                extra.getIn(["priority_stance_alignment", "alignment"])
            const currentAlignment =
                alignmentTypes.find((alignment) => alignment.get("id") === alignmentId) ||
                fromJS({
                    label: "No Alignment",
                    icon: "user-plus",
                    iconFamily: "fal",
                    id: 0,
                })

            // this is special logic for the alerts table because each row has an underlying object
            const quorumLink =
                feedItemCache &&
                feedItemCache.get("quorum_link") &&
                feedItemCache.get("quorum_link").replace("https://www.quorum.us", "")
            const underlyingDatumId = feedItemCache && getFeedItemUnderlyingItemId(quorumLink, underlyingQDT)
            if (
                quorumDataType === QuorumDataType.alert.value &&
                !feedItemDataTypesWithAlignment.includes(underlyingQDT)
            ) {
                return null
            }
            const finalQDT =
                (underlyingQDT && DjangIO.app.models.QuorumDataType.by_value(underlyingQDT)) ||
                DjangIO.app.models.QuorumDataType.by_value(quorumDataType)

            return (
                <div className={classNames("action-button", "search-table-dropdown")}>
                    <DropdownButton
                        text={currentAlignment.get("label")}
                        leftIcon={currentAlignment.get("icon")}
                        leftIconFamily={currentAlignment.get("iconFamily")}
                        menuOptions={alignmentTypes
                            .filter((alignment) => alignment.get("id") !== alignmentId)
                            .map((alignment) => ({
                                iconFamily: alignment.get("iconFamily"),
                                icon: alignment.get("icon"),
                                text: alignment.get("label"),
                                dataCy: alignment.get("label"),
                                onClick: () =>
                                    updateCellExtraAndPatchValue({
                                        updateResourceTableRow: props.updateResourceTableRow,
                                        tableId,
                                        rowId: id,
                                        patchValueFunction: props.postProfileAlignment,
                                        patchValueArgs: {
                                            qdt: finalQDT,
                                            profileId: underlyingDatumId || id,
                                            alignmentTypeId: alignment.get("id"),
                                        },
                                        updateExtraObj: { alignment: alignment.get("id") },
                                        updateExtraRollbackObj: { alignment: alignmentId },
                                    }),
                            }))}
                        type="resource-table-dropdown"
                        dataCy="search-table-alignment-dropdown"
                    />
                </div>
            )
        },
        width: 170,
        invert_sorting: true,
        resourceTableEnableSorting: !dataTypesWithoutColumnSorting.includes(quorumDataType),
        resourceTableDisableCellLink: true,
        columnSortingQuerymethod: "order_by_alignment",
    }),
)

export const selectStanceColumn = createSelector(
    (state, props) => props,
    selectQuorumDataType,
    selectTableId,
    (props, quorumDataType, tableId) => ({
        name: "Stance",
        field: ["id", "_extra", "data_type", "big_cache"],
        display: (id, extra, underlyingQDT, feedItemCache) => {
            const currentStanceValue =
                extra.get("stance") ||
                // for FeedItems we use dehydrate_extra_priority_stance_alignment
                extra.getIn(["priority_stance_alignment", "stance"]) ||
                DjangIO.app.userdata.types.LegislativeStanceType.no_stance.value

            // this is special logic for the alerts table because each row has an underlying object
            const quorumLink =
                feedItemCache &&
                feedItemCache.get("quorum_link") &&
                feedItemCache.get("quorum_link").replace("https://www.quorum.us", "")
            const underlyingDatumId = feedItemCache && getFeedItemUnderlyingItemId(quorumLink, underlyingQDT)
            // if it is an alert table, we only want to show a dropdown for relevant underlying data types
            if (quorumDataType === QuorumDataType.alert.value && !feedItemDataTypesWithStance.includes(underlyingQDT)) {
                return null
            }

            // if we expect a verified transcript, we do not display the stance button in Table View
            if (extra.getIn(["committee_meeting", "committee_meeting__generate_manual_transcript"])) {
                return null
            }

            const stanceBaseMenuOptions = props.stanceOptions

            const currentStanceEnum = stanceWithCustomLabelByValue(props.stanceOptions, currentStanceValue)

            return (
                <div className={classNames("action-button", "search-table-dropdown")}>
                    <DropdownButton
                        text={currentStanceEnum.label}
                        leftIcon={currentStanceEnum.fa5_icon}
                        leftIconFamily={
                            currentStanceValue === DjangIO.app.userdata.types.LegislativeStanceType.no_stance.value
                                ? "fal"
                                : "fas"
                        }
                        menuOptions={stanceBaseMenuOptions
                            .filter((stance) => stance.value !== currentStanceValue)
                            .filter((item) => item.value)
                            .sort(DjangIO.orderSort)
                            .map((stance) => ({
                                iconFamily: "fas",
                                icon: stance.fa5_icon,
                                text: stance.label,
                                dataCy: stance.key,
                                onClick: () =>
                                    updateCellExtraAndPatchValue({
                                        updateResourceTableRow: props.updateResourceTableRow,
                                        tableId,
                                        rowId: id,
                                        patchValueFunction: props.setOrCreateStance,
                                        patchValueArgs: {
                                            stance: stance.value,
                                            object_id: underlyingDatumId || id,
                                            quorum_data_type: underlyingQDT || quorumDataType,
                                        },
                                        updateExtraObj: { stance: stance.value },
                                        updateExtraRollbackObj: { stance: currentStanceValue },
                                    }),
                            }))}
                        isTiny
                        type="resource-table-dropdown"
                        dataCy="search-table-stance-dropdown"
                    />
                </div>
            )
        },
        width: columnWidthConstants.STANDARD_COLUMN_WIDTH,
        resourceTableEnableSorting: !dataTypesWithoutColumnSorting.includes(quorumDataType),
        resourceTableDisableCellLink: true,
        columnSortingQuerymethod: "order_by_stance",
    }),
)

export const selectIssuesColumn = createSelector(
    (state, props) => props,
    selectQuorumDataType,
    selectTableId,
    (props, quorumDataType, tableId) => ({
        name: "Issues",
        field: ["id", "_extra"],
        display: (id, extra) => {
            // articles has a separate dehydrate_issues method so we created a dehydate_extra_issues_for_resource_table method
            const issues = extra.has("issues") ? extra.get("issues") : extra.get("issues_for_resource_table")

            // if we expect a verified transcript and the feature flag is enabled, we do not display the issues button in Table View
            if (extra.getIn(["committee_meeting", "committee_meeting__generate_manual_transcript"])) {
                return null
            }

            return (
                <div className="action-button issues-select-column">
                    <ResourceTableIssueAdder
                        buttonTextWidth={`${columnWidthConstants.ISSUES_COLUMN_WIDTH - 100}px`}
                        issues={issues}
                        quorumDataType={quorumDataType}
                        rowId={id}
                        tableId={tableId}
                        updateResourceTableRow={props.updateResourceTableRow}
                    />
                </div>
            )
        },
        width: columnWidthConstants.ISSUES_COLUMN_WIDTH,
        resourceTableDisableCellLink: true,
    }),
)

export const selectAdvocateIsActiveColumn = createSelector(
    (state, props) => props,
    selectQuorumDataType,
    selectTableId,
    (props) => ({
        name: "Is Active",
        field: "last_activated_at",
        display: (date) => {
            if (!date) {
                return (
                    <div>
                        <Icon icon={"times"} /> No
                    </div>
                )
            }
            const today = new Date()
            const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
                .toISOString()
                .slice(0, 10)

            if (date > oneYearAgo) {
                return (
                    <div>
                        <Icon icon={"star"} color={colors.ALDEMainColor} iconFamily={"fas"} /> Yes
                    </div>
                )
            }
            return (
                <div>
                    <Icon icon={"times"} /> No
                </div>
            )
        },
        sortable: false,
        width: columnWidthConstants.STANDARD_COLUMN_WIDTH,
    }),
)

export const selectAdvocateLastKindOfAction = createSelector(
    (state, props) => props,
    selectQuorumDataType,
    selectTableId,
    () => ({
        name: "Last Kind of Action",
        field: "last_kind_action",
        display: (lastKindOfAction) => {
            if (!lastKindOfAction) {
                return <div />
            }

            const interactionType = DjangIO.app.userdata.types.GrassrootsInteractionType.by_value(lastKindOfAction)
            const campaignType = DjangIO.app.grassroots.campaign.types.CampaignType.items().filter(
                (campaignType) => campaignType.interaction_type === interactionType.value,
            )[0]

            if (!campaignType) {
                return <div />
            }

            return (
                <div>
                    <span class="icon-hover" style={{ color: colors.QuorumTheme.colors.primary[7] }}>
                        <Icon icon={campaignType.fa5_icon} iconFamily={"fal"} />
                    </span>
                    {interactionType.label}
                </div>
            )
        },
        sortable: false,
        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
    }),
)

export const selectAdvocateSubscriptionsColumn = createSelector(() => ({
    name: "Opted In",
    field: ["unsubscribed_from_bulk_emails", "unsubscribed_from_texting", "secondary_phone"],
    display: (unEmail, unText, phone) => {
        const divider = "  "
        return (
            <div>
                <span class={"icon-hover"} style={{ color: colors.QuorumTheme.colors.primary[7] }}>
                    <Icon icon={"envelope"} iconFamily={"fas"} />
                </span>
                {!unEmail ? "Yes" : "No"}
                {divider}
                <span class={"icon-hover"} style={{ color: colors.QuorumTheme.colors.primary[7] }}>
                    <Icon icon={"phone"} iconFamily={"fas"} />
                </span>
                {/* this mimics the logic found on the has_text_subscribed Supporter QueryMethod */}
                {phone && phone.length === 11 && phone[0] === "1" && unText === false ? "Yes" : "No"}
            </div>
        )
    },
    sortable: false,
    width: columnWidthConstants.STANDARD_COLUMN_WIDTH,
}))

const selectAdvocateGamificationLevelsColumn = createSelector(selectActionCentersGamified, (actionCenterList) => {
    // If there are more than 3 action centers, we don't want to show the columns
    if (actionCenterList.length > 3) {
        return []
    }

    return actionCenterList.map((actionCenter) => ({
        name: actionCenter.name,
        field: "_extra",
        display: (extra) => {
            return extra.get("action_center_level")?.get(actionCenter.name)?.get("level")
        },
    }))
})

export const selectColumns = createSelector(
    selectQuorumDataType,
    selectActionColumn,
    selectPriorityColumn,
    selectAlignmentColumn,
    selectStanceColumn,
    selectIssuesColumn,
    selectIsPacPersona,
    selectIsPacHybridPersona,
    selectHasMultipleBankAccounts,
    selectAdvocateIsActiveColumn,
    selectAdvocateLastKindOfAction,
    selectAdvocateSubscriptionsColumn,
    selectAdvocateGamificationLevelsColumn,
    (
        quorumDataType,
        actionColumn,
        priorityColumn,
        alignmentColumn,
        stanceColumn,
        issuesColumn,
        isPacPersona, // The persona of the current user
        isPacHybridPersona,
        hasMultipleBankAccounts,
        advocateIsActiveColumn,
        advocateLastKindOfAction,
        advocateSubscriptionsColumn,
        advocateGamificationLevelsColumn,
    ) => {
        switch (quorumDataType) {
            case QuorumDataType.alert.value:
                return [
                    getBasicColumnConfig({
                        name: "Region(s)",
                        field: "regions",
                        display: (regions) =>
                            regions &&
                            regions
                                .map(
                                    (region) =>
                                        DjangIO.app.models.Region.by_value(region) &&
                                        DjangIO.app.models.Region.by_value(region).region_name,
                                )
                                .join(", "),
                    }),
                    getBasicColumnConfig({
                        name: "Type",
                        field: "data_type",
                    }),
                    stanceColumn,
                    priorityColumn,
                    alignmentColumn,
                ]
            case QuorumDataType.amendment.value:
                return [
                    getBasicColumnConfig({
                        name: "Bill Name",
                        field: "bill_name",
                        sortable: true,
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "status_date",
                    }),
                    getBasicColumnConfig({
                        name: "Sponsor",
                        field: "sponsor_name",
                        width: 250,
                    }),
                    stanceColumn,
                    priorityColumn,
                    issuesColumn,
                ]
            case QuorumDataType.attachment.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Uploaded By",
                        field: "user",
                        display: (user) => user.get("name"),
                        sortOn: "user__first_name",
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "created",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Size",
                        field: "size",
                    }),
                ]
            case QuorumDataType.bill.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Status",
                        field: "current_status_text",
                        sortable: true,
                        sortOn: "current_status",
                    }),
                    getBasicColumnConfig({
                        name: "Official Description",
                        field: "_extra",
                        display: (extra) => cleanUpHtml(extra.get("official_description")),
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    ...(hasAnyPermission([PermissionType.hide_stance]) ? [] : [stanceColumn]),
                    ...(hasAnyPermission([PermissionType.hide_priority]) ? [] : [priorityColumn]),
                    issuesColumn,
                    getBasicColumnConfig({
                        name: "Type",
                        field: "bill_type",
                    }),
                    getBasicColumnConfig({
                        name: "Last Timeline Action",
                        field: "last_action_text",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getDateColumnConfig({
                        name: "Last Timeline Action Date",
                        field: "most_recent_action_date",
                        sortable: true,
                        width: 200,
                    }),
                    getDateColumnConfig({
                        name: "Introduced Date",
                        field: "introduced_date",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Number",
                        field: "number",
                        columnSortingQuerymethod: "order_by_number",
                    }),
                ]
            case QuorumDataType.caucus.value:
                return [
                    getBasicColumnConfig({
                        name: "Chair",
                        field: "chairs_string",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Ranking Member",
                        field: "ranking_members_string",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Phone",
                        field: "phone",
                    }),
                    getBasicColumnConfig({
                        name: "Address",
                        field: "address",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                ]
            case QuorumDataType.committee.value:
                return [
                    getBasicColumnConfig({
                        name: "Region",
                        field: "region",
                        enumLabel: "region_name",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Chair",
                        field: "chairs_string",
                        sortable: true,
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Ranking Member",
                        field: "ranking_members_string",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Phone",
                        field: "phone",
                    }),
                    getBasicColumnConfig({
                        name: "Address",
                        field: "address",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                ]
            case QuorumDataType.docket.value:
                return [
                    getBasicColumnConfig({
                        name: "Docket Type",
                        field: "docket_type",
                        sortable: true,
                        width: 275,
                    }),
                    getBasicColumnConfig({
                        name: "Docket ID",
                        field: "docket_id",
                        sortable: true,
                        width: 220,
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "created",
                        sortable: true,
                    }),
                ]
            case QuorumDataType.document.value:
                return [
                    getBasicColumnConfig({
                        name: "Region",
                        field: "region",
                        enumLabel: "region_name",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Type",
                        field: "document_type",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Source Name",
                        field: "source_name",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "date",
                        sortable: true,
                    }),
                    stanceColumn,
                    issuesColumn,
                ]
            case QuorumDataType.event.value:
                return [
                    getBasicColumnConfig({
                        name: "Region",
                        field: "region",
                        enumLabel: "region_name",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Chamber",
                        field: ["region", "chamber"],
                        display: (region, chamber) => getEventChamber(region, chamber),
                        width: columnWidthConstants.STANDARD_COLUMN_WIDTH,
                        sortOn: "chamber",
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "when",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Time",
                        field: ["when", "is_custom_all_day_event", "region"],
                        display: (dateTime, allDay, region) => {
                            if (allDay) return "All Day"
                            if (!dateTime) return ""

                            const regionObj = region && DjangIO.app.models.Region.by_value(region)
                            const timezone = regionObj
                                ? parseTimezoneNameFromAbbrev(regionObj.timezone)
                                : "America/New_York"
                            return parseEstDateToTimezone(dateTime, timezone).format("h:mma")
                        },
                        sortOn: "when",
                    }),
                ]
            case QuorumDataType.election.value:
                return [
                    getBasicColumnConfig({
                        name: "State",
                        field: "region_name",
                    }),
                    getBasicColumnConfig({
                        name: "Type",
                        field: "election_type",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "election_date",
                    }),
                ]
            case QuorumDataType.grassroots_action.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Contact",
                        field: "_extra",
                        display: (extra) => extra.get("supporter_name"),
                        sortOn: "supporter__name",
                    }),
                    getBasicColumnConfig({
                        name: "Type",
                        field: "supporter_action_type",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Campaign Target",
                        field: "_extra",
                        display: (extra) => extra.get("person_name"),
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "date",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Content",
                        field: ["subject", "text", "_extra"],
                        display: (subject, text, extra) => {
                            if (
                                isFeatureEnabled("ff_pac_charitable_match_search") &&
                                Userdata.Permissions.qp_pac_match &&
                                (Userdata.can_view_pac_receipts || Userdata.can_alter_pac_transactions) &&
                                extra.get("charity_name")
                            ) {
                                return `Charity Name: ${extra.get("charity_name")}`
                            }
                            const cellText = subject ? `${subject}: ${text}` : text
                            return cleanUpHtml(cellText)
                        },
                        sortable: true,
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    ...(Userdata.can_view_pac_receipts || Userdata.can_alter_pac_transactions
                        ? [
                              getBasicColumnConfig({
                                  name: "Payment Type",
                                  field: "transaction_method_type",
                              }),
                              getBasicColumnConfig({
                                  name: "Amount",
                                  field: "amount",
                                  display: (amount) => (amount ? `$${amount / 100}` : ""),
                              }),
                          ]
                        : []),
                ]
            case QuorumDataType.media.value:
                return [
                    getBasicColumnConfig({
                        name: "Source",
                        field: "source",
                        display: (source) => source.get("name"),
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "estimated_published_date",
                    }),
                    stanceColumn,
                    priorityColumn,
                    issuesColumn,
                ]
            case QuorumDataType.note.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Type",
                        field: "note_type",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Start Date",
                        field: ["date", "note_type"],
                        display: (date, noteType) =>
                            date && noteType === DjangIO.app.userdata.types.NoteType.interaction.value
                                ? moment(date).local().format(sheetConstants.DATE_FORMAT)
                                : undefined,
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Start Time",
                        field: ["searchable_date", "note_type"],
                        display: (searchableDate, noteType) =>
                            noteType === DjangIO.app.userdata.types.NoteType.interaction.value && searchableDate
                                ? searchableDate.split(", ").slice(-1)[0]
                                : undefined,
                        sortable: true,
                    }),
                    getDateColumnConfig({
                        name: "Created Date",
                        field: "created",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Created Time",
                        field: "searchable_created_date",
                        display: (searchableDate) => searchableDate && searchableDate.split(", ").slice(-1)[0],
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Duration",
                        field: "duration",
                    }),
                    getBasicColumnConfig({
                        name: "Creator",
                        field: "cache_json",
                        display: (cacheJson) =>
                            cacheJson.getIn(["user", "name"]) || cacheJson.getIn(["user", "username"]) || "Quorum",
                    }),
                    getBasicColumnConfig({
                        name: "Participant(s)",
                        field: "cache_json",
                        display: (cacheJson) => {
                            if (cacheJson) {
                                const teamMembers = cacheJson
                                    .get("team_members")
                                    .map((teamMember) => teamMember.get("name"))
                                const supporters = cacheJson.get("supporters").map((supporter) => supporter.get("name"))
                                const members = cacheJson.get("members").map((member) => member.get("name"))
                                return [...teamMembers, ...supporters, ...members].join(", ")
                            }
                            return undefined
                        },
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                ]
            case QuorumDataType.pacc_pac_office.value:
                return [
                    getBasicColumnConfig({
                        name: "Name",
                        field: "name",
                        sortable: true,
                        width: 500,
                    }),
                    getBasicColumnConfig({
                        name: "Jurisdiction",
                        field: "juris",
                        sortable: true,
                    }),
                ]
            case QuorumDataType.pacc_pac_organization.value:
                return [
                    getBasicColumnConfig({
                        name: "Candidate Name",
                        field: "candidate_full_name",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Office",
                        field: "_extra",
                        display: (extra) => extra.getIn(["foreign_key_names", "office__name"]),
                        sortOn: "office__name",
                    }),
                    getBasicColumnConfig({
                        name: "Office State",
                        field: "office_state",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Election Year",
                        field: "reelection_year",
                        display: (reelectionYear) => (reelectionYear ? reelectionYear : ""),
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Filer ID",
                        field: "filer_id",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Candidate Status",
                        field: "is_incumbent",
                        display: (isIncumbent) => (isIncumbent ? "Incumbent" : "Challenger"),
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Party",
                        field: "party",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "District",
                        field: "office_district",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Limit Type",
                        field: "pac_limit_type",
                        sortable: true,
                    }),
                ]
            case QuorumDataType.pacc_pac_transaction.value:
                return [
                    getBasicColumnConfig({
                        name: "ID",
                        field: "pac_transaction_id",
                        sortable: true,
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "transaction_date",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Amount",
                        field: "display_amount",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Type",
                        field: "transaction_type",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Transaction Type",
                        field: "pac_report_code",
                        sortable: true,
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Election",
                        field: "_extra",
                        display: (extra) => extra.getIn(["foreign_key_names", "pac_classic_election__name"]),
                        sortable: true,
                        sortOn: "election__name",
                    }),
                    getBasicColumnConfig({
                        name: "Candidate",
                        field: "_extra",
                        display: (extra) =>
                            [
                                extra.getIn([
                                    "foreign_key_names",
                                    "pac_classic_pac_account__pac_classic_pac_organization__candidate_first_name",
                                ]),
                                extra.getIn([
                                    "foreign_key_names",
                                    "pac_classic_pac_account__pac_classic_pac_organization__candidate_last_name",
                                ]),
                            ]
                                .filter(Boolean)
                                .join(" "),
                        sortable: false,
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Method",
                        field: "transaction_method",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Reconciled",
                        field: "is_cleared",
                        display: (reconciled) => (reconciled ? "Yes" : "No"),
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Internal Comment",
                        field: "internal_comment",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                ]
            case QuorumDataType.person.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Title",
                        field: "title",
                    }),
                    getBasicColumnConfig({
                        name: "Org",
                        field: "public_organization_descriptor",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Region",
                        field: "most_recent_region",
                        enumLabel: "region_name",
                        sortable: true,
                    }),
                    ...(hasAnyPermission([PermissionType.hide_alignment]) ? [] : [alignmentColumn]),
                    ...(hasAnyPermission([PermissionType.hide_priority]) ? [] : [priorityColumn]),
                    issuesColumn,
                ]
            case QuorumDataType.political_committee.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Candidate",
                        field: "candidate_name",
                        width: 250,
                    }),
                    getBasicColumnConfig({
                        name: "Office",
                        field: "candidate_office_string",
                    }),
                    getBasicColumnConfig({
                        name: "Election Year",
                        field: "election_year",
                    }),
                    getBasicColumnConfig({
                        name: "State",
                        field: "candidate_state_region",
                        display: (regionId) => {
                            if (!regionId) {
                                // usually null
                                return undefined
                            }
                            const region = DjangIO.app.models.SupportedRegion.by_value(regionId)
                            return region ? region.region_name : `Unknown (${regionId})`
                        },
                    }),
                    getBasicColumnConfig({
                        name: "FEC ID",
                        field: "fec_committee_id",
                    }),
                    getBasicColumnConfig({
                        name: "Candidate Status",
                        field: "candidate_status",
                    }),
                    getBasicColumnConfig({
                        name: "District",
                        field: "district_name",
                        width: 250,
                    }),
                    getBasicColumnConfig({
                        name: "Party",
                        field: "party_label",
                        width: 200,
                    }),
                    getBasicColumnConfig({
                        name: "Address",
                        field: ["addr_city", "addr_state", "addr_street1", "addr_street2", "addr_zip"],
                        display: (city, state, street1, street2, zip) =>
                            [street1, street2, city, state, zip].filter(Boolean).join(", "),
                        width: 300,
                    }),
                    getBasicColumnConfig({
                        name: "Committee Type",
                        field: "committee_type",
                        width: 250,
                    }),
                ]
            case QuorumDataType.press_contact.value:
                return [
                    getBasicColumnConfig({
                        name: "Title",
                        field: "title",
                    }),
                    getBasicColumnConfig({
                        name: "Outlet(s)",
                        field: "public_organization_descriptor",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Email",
                        field: "email",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Phone Number",
                        field: "most_recent_role_phone",
                        display: (phone) => phone && formatPhoneNumber(phone),
                    }),
                    getBasicColumnConfig({
                        name: "Location",
                        field: ["most_recent_country__name", "most_recent_region"],
                        display: (country_name, region) => {
                            if (country_name === "United States") {
                                // if in the US, show state name
                                return DjangIO.app.models.Region.by_value(region).region_name
                            }
                            return country_name || DjangIO.app.models.Region.by_value(region).region_name
                        },
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Beats",
                        field: "_extra",
                        display: (extra) => {
                            const beats = extra.get("beats") || []

                            if (beats && beats.reduce) {
                                return beats.reduce((previousString, beat) => {
                                    const beatName = beat.get("beat__name", "")

                                    return previousString ? `${previousString}, ${beatName}` : beatName
                                }, "")
                            }

                            return ""
                        },
                    }),
                    alignmentColumn,
                    priorityColumn,
                    issuesColumn,
                ]
            case QuorumDataType.public_organization.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Address",
                        field: ["billing_address", "shipping_address"],
                        // Billing Address takes precedence over Shipping
                        display: (billingAddress, shippingAddress) => billingAddress || shippingAddress || "",
                        width: 300,
                    }),
                    getBasicColumnConfig({
                        name: "PAC Org. Type",
                        field: "pac_org_type",
                        width: 200,
                    }),
                    getBasicColumnConfig({
                        name: "Type",
                        field: "organization_type",
                        sortable: true,
                    }),
                    issuesColumn,
                ]
            case QuorumDataType.regulation.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Region",
                        field: "region",
                        enumLabel: "region_name",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Status",
                        field: "general_status",
                        sortable: true,
                    }),
                    stanceColumn,
                    priorityColumn,
                    issuesColumn,
                    getBasicColumnConfig({
                        name: "Last Timeline Action",
                        field: "detailed_status",
                    }),
                    getDateColumnConfig({
                        name: "Last Timeline Action Date",
                        field: "detailed_status_date",
                        sortable: true,
                        width: 200,
                    }),
                    getDateColumnConfig({
                        name: "Effective Date",
                        field: "effective_date",
                        sortable: true,
                    }),
                ]
            case QuorumDataType.send_email.value:
                return [
                    getBasicColumnConfig({
                        name: "Subject",
                        field: "_extra",
                        display: (extra) => extra.getIn(["bulk_email", "bulk_email__subject"]),
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Outbox Sender",
                        field: "_extra",
                        display: (extra) => extra.getIn(["bulk_email", "bulk_email__from_name"]),
                    }),
                ]
            case QuorumDataType.staffer.value:
                return [
                    actionColumn,
                    getBasicColumnConfig({
                        name: "Title",
                        field: "title",
                    }),
                    getBasicColumnConfig({
                        name: "Org",
                        field: "public_organization_descriptor",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Region",
                        field: "most_recent_region",
                        enumLabel: "region_name",
                        sortable: true,
                    }),
                    ...(hasAnyPermission([PermissionType.hide_alignment]) ? [] : [alignmentColumn]),
                    ...(hasAnyPermission([PermissionType.hide_priority]) ? [] : [priorityColumn]),
                    issuesColumn,
                ]
            case QuorumDataType.supporter.value:
                if (isGrassrootsPersona()) {
                    return [
                        getBasicColumnConfig({
                            name: "Region",
                            field: "region",
                            enumLabel: "region_name",
                            sortable: true,
                        }),
                        getBasicColumnConfig({
                            name: "Email",
                            field: "email",
                            width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                        }),
                        advocateSubscriptionsColumn,
                        advocateIsActiveColumn,
                        ...advocateGamificationLevelsColumn,
                        getBasicColumnConfig({
                            name: "Number of Actions",
                            field: "num_actions",
                            sortable: true,
                            display: (numActions) => <NumberCounterCell value={numActions} />,
                            width: 200,
                        }),
                        advocateLastKindOfAction,
                        getDateColumnConfig({
                            name: "Last Action Date",
                            field: "last_action_date",
                            sortable: true,
                            width: 200,
                        }),
                        getBasicColumnConfig({
                            name: "Campaigns Interacted In",
                            field: "num_campaigns_interacted",
                            sortable: true,
                            display: (numCampaignsInteracted) => <NumberCounterCell value={numCampaignsInteracted} />,
                            width: 220,
                        }),
                        getBasicColumnConfig({
                            name: "Org",
                            field: "public_organization_names",
                            width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                        }),
                        alignmentColumn,
                        priorityColumn,
                        issuesColumn,
                    ]
                }
                // Columns in the table depend on whether the user is a PAC Manager persona user for a PAC_HYBRID client
                if (isPacHybridPersona) {
                    return [
                        getBasicColumnConfig({
                            name: "Unique Identifier",
                            field: "unique_identifier",
                            sortable: true,
                            width: 160,
                        }),
                        getBasicColumnConfig({
                            name: "Occupation",
                            field: "occupation",
                            sortable: true,
                        }),
                        getBasicColumnConfig({
                            name: "Employer",
                            field: "employer",
                            sortable: true,
                        }),
                        getBasicColumnConfig({
                            name: "YTD Total",
                            field: "_extra",
                            sortable: true,
                            display: (extra) => {
                                const ytdAmount = extra.getIn(["pac2_ytd_total", "pac_received_amount_ytd"]) ?? 0
                                return formatAmount(ytdAmount)
                            },
                        }),
                        getBasicColumnConfig({
                            name: "Last YTD Total",
                            field: "_extra",
                            sortable: true,
                            display: (extra) => {
                                const lytdAmount = extra.getIn(["pac2_ytd_total", "pac_received_amount_lytd"]) ?? 0
                                return formatAmount(lytdAmount)
                            },
                        }),
                        getBasicColumnConfig({
                            name: "Last Receipt Amount",
                            field: "_extra",
                            sortable: true,
                            display: (extra) => {
                                const lastAmount = extra.getIn(["pac2_ytd_total", "pac_receipt_last_amount"]) ?? 0
                                return formatAmount(lastAmount)
                            },
                        }),
                        getBasicColumnConfig({
                            name: "Last Receipt Date",
                            field: "_extra",
                            sortable: true,
                            display: (extra) => {
                                const lastDate = extra.getIn(["pac2_ytd_total", "pac_receipt_last_date"])
                                return lastDate ? moment(lastDate).local().format(sheetConstants.DATE_FORMAT) : ""
                            },
                        }),
                        getBasicColumnConfig({
                            name: "Home Address",
                            field: [
                                "pac2_home_address_line_1",
                                "pac2_home_address_line_2",
                                "pac2_home_city",
                                "pac2_home_state",
                                "pac2_home_zip",
                            ],
                            width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                            sortOn: "pac2_home_address_line_1",
                            display: (
                                pac2_home_address_line_1,
                                pac2_home_address_line_2,
                                pac2_home_city,
                                pac2_home_state,
                                pac2_home_zip,
                            ) => {
                                const address = [
                                    pac2_home_address_line_1,
                                    pac2_home_address_line_2,
                                    pac2_home_city,
                                    pac2_home_state,
                                ]
                                    .filter((val) => val)
                                    .join(", ")
                                return pac2_home_zip ? `${address} ${pac2_home_zip}` : address
                            },
                        }),
                        getBasicColumnConfig({
                            name: "Home State",
                            field: "pac2_home_state",
                            sortable: true,
                            width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                        }),
                        getBasicColumnConfig({
                            name: "Organization",
                            field: "pac2_organization_name",
                            sortable: true,
                            width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                        }),
                    ]
                }
                return [
                    actionColumn,
                    ...(isPacPersona
                        ? [
                              getBasicColumnConfig({
                                  name: "Unique Identifier",
                                  field: "unique_identifier",
                                  sortable: true,
                                  width: 160,
                              }),
                          ]
                        : []),
                    getBasicColumnConfig({
                        name: "Title",
                        field: "title",
                        sortable: true,
                    }),
                    ...(isPacPersona
                        ? [
                              getBasicColumnConfig({
                                  name: "Employer",
                                  field: "employer",
                                  sortable: true,
                              }),
                              getBasicColumnConfig({
                                  name: "YTD Total",
                                  field: "_extra",
                                  display: (extra) => extra.get("ytd_total") || `$0.00`,
                              }),
                              getBasicColumnConfig({
                                  name: "Last YTD Total",
                                  field: "_extra",
                                  display: (extra) => extra.get("last_ytd_total") || `$0.00`,
                              }),
                              getBasicColumnConfig({
                                  name: "Home Address",
                                  field: "address",
                                  sortable: true,
                                  width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                              }),
                          ]
                        : []),
                    getBasicColumnConfig({
                        name: "Email",
                        field: "email",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getBasicColumnConfig({
                        name: "Org",
                        field: "public_organization_names",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    ...(isPacPersona
                        ? [
                              getBasicColumnConfig({
                                  name: "PAC Eligible",
                                  field: "_extra",
                                  display: (extra) => extra.get("pac_eligibilities"),
                              }),
                          ]
                        : []),
                    getBasicColumnConfig({
                        name: "Region",
                        field: "region",
                        enumLabel: "region_name",
                        sortable: true,
                    }),
                    alignmentColumn,
                    priorityColumn,
                    issuesColumn,
                ]
            case QuorumDataType.transaction.value:
                // For column order, follow "Disbursements" mode: https://quorumanalytics.atlassian.net/browse/QPAC-791
                return [
                    getDateColumnConfig({
                        name: "Date",
                        field: "occurred_date",
                        sortable: true,
                    }),
                    getDollarsColumnConfig({
                        name: "Amount",
                        field: "raw_amount",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Candidate",
                        field: "candidate_name",
                        sortable: true,
                        width: 250,
                    }),
                    getBasicColumnConfig({
                        name: "Type",
                        field: ["transaction_type", "transaction_special_type"],
                        columnSortingQuerymethod: "order_by_transaction_type",
                        display: (type, specialType) => <TransactionTypeCell type={type} specialType={specialType} />,
                        width: 200,
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Transaction Type",
                        field: "contribution_type_label",
                        width: 250,
                    }),
                    getBasicColumnConfig({
                        name: "Election",
                        field: "election_name",
                        width: 200,
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Voided",
                        field: "is_voided",
                        display: (voided) => (voided ? "Yes" : "No"),
                        sortable: true,
                        columnSortingQuerymethod: "order_by_is_voided",
                    }),
                    getBasicColumnConfig({
                        name: "Void Date",
                        field: "void_date",
                        display: (date) => (date ? moment(date).local().format(sheetConstants.DATE_FORMAT) : ""),
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Description",
                        field: "description",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Method",
                        // If field is not an array, the column is left blank -- but only for transaction_method. I don't know why.
                        field: ["transaction_method"],
                        display: (method) => (
                            <IconTypeCell value={method} djangioType={DjangIO.app.ledger.types.TransactionMethodType} />
                        ),
                        width: 225,
                        sortable: true,
                        sortOn: "transaction_method",
                    }),
                    getBasicColumnConfig({
                        name: "Ref. Number",
                        field: "reference_number",
                        sortable: true,
                    }),
                    ...(hasMultipleBankAccounts
                        ? [
                              getBasicColumnConfig({
                                  name: "Bank Account",
                                  field: "bank_account_for_transaction",
                                  width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                              }),
                          ]
                        : []),
                    getBasicColumnConfig({
                        name: "Transaction ID",
                        field: "serial_number",
                    }),
                    getBasicColumnConfig({
                        name: "Payroll Cycle",
                        field: "payroll_frequency_type",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Internal Comment",
                        field: "additional_notes",
                    }),
                    getBasicColumnConfig({
                        name: "Reconciled",
                        field: "is_reconciled",
                        display: (reconciled) => (reconciled ? "Yes" : "No"),
                        sortable: true,
                    }),
                ]
            case QuorumDataType.vote.value:
                return [
                    getBasicColumnConfig({
                        name: "Region",
                        field: "region",
                        enumLabel: "region_name",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Chamber",
                        field: "chamber",
                        sortable: true,
                    }),
                    getBasicColumnConfig({
                        name: "Bill",
                        field: "related_bill",
                        display: (bill) => bill && bill.get("title"),
                        sortOn: "related_bill__title",
                        width: columnWidthConstants.WIDE_COLUMN_WIDTH,
                    }),
                    getDateColumnConfig({
                        name: "Date",
                        field: "created",
                    }),
                    getBasicColumnConfig({
                        name: "Result",
                        field: "result",
                        width: 200,
                    }),
                    stanceColumn,
                    issuesColumn,
                ]
            case QuorumDataType.receipt.value:
                return [
                    getBasicColumnConfig({
                        name: "Recipient",
                        field: "super_committee_recipient_name",
                        sortable: false,
                        width: 350,
                    }),
                    getBasicColumnConfig({
                        name: "Amount",
                        field: "amount",
                        sortable: true,
                        display: (amount) => formatAmount(amount),
                    }),
                    getBasicColumnConfig({
                        name: "Date",
                        field: "date",
                        sortable: true,
                        display: (date) => (date ? formatDate(date) : date),
                    }),
                    getBasicColumnConfig({
                        name: "Election Type",
                        field: "election_type_full",
                        sortable: false,
                    }),
                    getBasicColumnConfig({
                        name: "Recipient Party",
                        field: "super_committee_recipient_party_type",
                        sortable: false,
                        display: (party) => capitalizeFirstLetter(party),
                    }),
                    actionColumn,
                ]
            case QuorumDataType.disbursement.value:
                return [
                    getBasicColumnConfig({
                        name: "Recipient",
                        field: "recipient_name",
                        sortable: false,
                        width: 350,
                    }),
                    getBasicColumnConfig({
                        name: "Amount",
                        field: "amount",
                        sortable: true,
                        display: (amount) => formatAmount(amount),
                    }),
                    getBasicColumnConfig({
                        name: "Date",
                        field: "date",
                        sortable: true,
                        display: (date) => (date ? formatDate(date) : date),
                    }),
                    getBasicColumnConfig({
                        name: "Region",
                        field: "recipient_state_code",
                        sortable: false,
                    }),
                    getBasicColumnConfig({
                        name: "Description",
                        field: "disbursement_description",
                        sortable: false,
                        width: 400,
                    }),
                    actionColumn,
                ]
            default:
                return []
        }
    },
)

export const selectPermanentFilters = createSelector(selectSearchFlvSlice, (flv) => flv.get("permanentFilters"))

export const selectRowFilter = createSelector(
    selectQuorumDataType,
    selectCurrentFilters,
    selectPermanentFilters,
    selectIsPacPersona,
    (quorumDataType, currentFilters, permanentFilters, isPacPersona) => {
        const updatedFilters = currentFilters.merge(permanentFilters)
        switch (quorumDataType) {
            case QuorumDataType.amendment.value:
            case QuorumDataType.regulation.value:
                return updatedFilters.set("dehydrate_extra", List(["stance", "priority", "issues"]))
            case QuorumDataType.bill.value:
                return updatedFilters.set(
                    "dehydrate_extra",
                    List(["stance", "priority", "issues", "official_description"]),
                )
            case QuorumDataType.document.value:
                return updatedFilters.set(
                    "dehydrate_extra",
                    // we now dehydrate committee_meeting for documents to check if committee expects verified transcripts
                    List(["committee_meeting", "stance", "issues"]),
                )
            case QuorumDataType.vote.value:
                return updatedFilters.set("dehydrate_extra", List(["stance", "issues"]))
            case QuorumDataType.grassroots_action.value:
                return updatedFilters.set("dehydrate_extra", List(["person_name", "supporter_name", "charity_name"]))
            case QuorumDataType.media.value:
                return updatedFilters.set("dehydrate_extra", List(["stance", "priority", "issues_for_resource_table"]))
            case QuorumDataType.person.value:
                return updatedFilters.set(
                    "dehydrate_extra",
                    List(["most_recent_role", "alignment", "priority", "issues"]),
                )
            case QuorumDataType.public_organization.value:
                return updatedFilters.set("dehydrate_extra", List(["issues"]))
            case QuorumDataType.send_email.value:
                return updatedFilters.set("dehydrate_extra", List(["bulk_email"]))
            case QuorumDataType.staffer.value: {
                const filters = updatedFilters.set("dehydrate_extra", List(["alignment", "priority", "issues"]))
                return filters.set("hygiene_staffer_types", true)
            }
            case QuorumDataType.supporter.value:
                return updatedFilters.set(
                    "dehydrate_extra",
                    List([
                        "alignment",
                        "priority",
                        "issues",
                        ...(isPacPersona ? ["last_ytd_total", "pac_eligibilities", "ytd_total", "pac2_ytd_total"] : []),
                        ...(isGrassrootsPersona() ? ["action_center_level"] : []),
                    ]),
                )
            case QuorumDataType.pacc_pac_organization.value:
                return updatedFilters.set("dehydrate_extra", List(["foreign_key_names"]))
            default:
                return updatedFilters
        }
    },
)

export const selectDatumBadgeConfig = createSelector(selectQuorumDataType, (quorumDataType) => {
    // when remediating ff, just remove note value from dataTypesWithoutRowLinks
    if (isFeatureEnabled("ff_view_only_note") && quorumDataType === DjangIO.app.models.QuorumDataType.note.value) {
        return {
            class: "view-sheet",
            shouldShow: true,
        }
    }

    if (dataTypesWithoutRowLinks.includes(quorumDataType)) {
        return undefined
    }
    return {
        class: "view-sheet",
        shouldShow: true,
    }
})

export const selectDisableRowLink = createSelector(selectQuorumDataType, (quorumDataType) => {
    // when remediating ff, just remove note value from dataTypesWithoutRowLinks
    if (isFeatureEnabled("ff_view_only_note") && quorumDataType === DjangIO.app.models.QuorumDataType.note.value) {
        return false
    }

    if (dataTypesWithoutRowLinks.includes(quorumDataType)) {
        return true
    }
    return false
})

export const selectDatumColumnSortingQuerymethod = createSelector(selectQuorumDataType, (quorumDataType) => {
    switch (quorumDataType) {
        case QuorumDataType.attachment.value:
        case QuorumDataType.committee.value:
            return "order_by_name"
        case QuorumDataType.bill.value:
            return "order_by_name_and_number"
        case QuorumDataType.docket.value:
        case QuorumDataType.regulation.value:
            return "order_by_title"
        case QuorumDataType.supporter.value:
            return "order_by_last_then_first_name"
        default:
            return undefined
    }
})

export const selectScrollInterval = createSelector(selectQuorumDataType, (quorumDataType) => {
    switch (quorumDataType) {
        case QuorumDataType.send_email.value:
        case QuorumDataType.vote.value:
            return 50
        case QuorumDataType.amendment.value:
        case QuorumDataType.attachment.value:
        case QuorumDataType.document.value:
        case QuorumDataType.grassroots_action.value:
        case QuorumDataType.note.value:
        case QuorumDataType.person.value:
        case QuorumDataType.regulation.value:
        case QuorumDataType.staffer.value:
        case QuorumDataType.supporter.value:
            return 100
        case QuorumDataType.receipt.value:
            return 50
        case QuorumDataType.disbursement.value:
            return 50
        default:
            return 250
    }
})

export const searchTableConnection = createStructuredSelector({
    columns: selectColumns,
    dataType: selectQuorumDataType,
    datumColumnSortingQuerymethod: selectDatumColumnSortingQuerymethod,
    disableRowLink: selectDisableRowLink,
    rowFilter: selectRowFilter,
    scrollInterval: selectScrollInterval,
    tableId: selectTableId,
})
